/**
 * 认证 Hook - 管理用户登录状态
 */
import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react'
import { authService, UserInfo } from '../services/authService'

interface AuthContextType {
  user: UserInfo | null
  loading: boolean
  isAuthenticated: boolean
  login: (phone: string, code: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    try {
      const profile = await authService.getProfile()
      setUser(profile)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token')
    if (token) {
      refreshUser()
    } else {
      setLoading(false)
    }

    // 监听Token过期事件
    const handleAuthExpired = () => {
      setUser(null)
      authService.logout()
    }
    window.addEventListener('auth:expired', handleAuthExpired)

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired)
    }
  }, [refreshUser])

  const login = useCallback(async (phone: string, code: string) => {
    const result = await authService.loginByPhone({ phone, code })
    setUser(result.user)
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default useAuth
